import React, { Suspense, lazy, useEffect } from 'react'
import './App.css'
import { Routes, Route, Navigate } from 'react-router-dom'
import TagManager from 'react-gtm-module'
import { gclidFromCookie, fbclidFromCookie, fbcFromCookie, fbpFromCookie } from './utils/attribution'
import PrivateRoute from './routes/PrivateRoute'
import GuestRoute from './routes/GuestRoute'
import Introduction from './components/introduction'
import Story from './components/story/Story'
import Quiz from './components/quiz/Quiz'
import Question from './components/question'
import { NotFound } from './components/404/404'
import StoryFinished from './components/story-finished'
import Dashboard from 'components/dashboard'
import CreateMeet from 'components/meet/CreateMeet'
import Meet from 'components/meet/Meet'

import {
  NOT_FOUND,
  STORY_FINISHED,
  INTRODUCTION,
  LOGIN,
  EMAIL_LINK_LOGIN,
  OFFER,
  REFERRAL_LANDING,
  SCORECARD,
  REFER,
  STORY,
  QUIZ,
  QUESTION,
  DASHBOARD,
  DASHBOARD_CLASSIC,
  CREATE_MEET,
  LIVE_MEET,
  PRO_MEET,
  CORRECTIONS,
  DISCUSSION,
  TASTER,
  CLAIM_TRIAL,
  CLAIM_OFFER,
  CLAIM_TRIAL_OFFER,
  CLAIM_FREE_TRIAL,
  HISTORY,
  CHOOSE_LANGUAGE,
  NEW_USER_PATH,
  OLD_USER_PATH,
  LEAD_USER_PATH,
  STORIES,
  HOME,
  USER,
  SIGNUP,
  PAYMENT_SUCCESSFUL,
  PAYMENT_FAILED,
  SETTING,
  EDITOR,
  NORTHSTAR,
  CREATOR_PERFORMANCE,
  TRANSLATIONS_REVIEW,
  WORDS,
} from 'routes/routeConstant'
import Corrections from 'components/Corrections'
import DiscussionPage from 'components/discussion/DiscussionPage'
import Taster from 'components/taster'
import ClaimTrial from 'components/claim/ClaimTrial'
import ClaimOffer from 'components/claim/ClaimOffer'
import ClaimTrialOffer from 'components/claim/ClaimTrialOffer'
import ClaimFreeTrial from './components/claim/ClaimFreeTrial'
import ReferralLanding from './components/referral/ReferralLanding'
import ReferPage from './components/referral/ReferPage'
import SkritterCallback from 'components/skritter/CallbackComponent'
import ChooseLanguage from 'components/choose-language'
import OnBoarding from 'components/onboarding'
import History from 'components/stories/History'
import Stories from 'components/landing-page'
import { REACT_APP_GTM_ID } from './utils/config'
import { AppUseSelector } from './redux/hooks'
import posthog from 'posthog-js'
import { getPlanInfo, membershipNameType } from './utils/constant'
import Login from 'components/auth/login'
import EmailLinkLogin from 'components/auth/email-link'
import Offer from 'components/offer'
import Scorecard from 'components/scorecard'
import PaymentFailed from 'components/setting/plan-tab/PaymentFailed'
import PaymentSuccessful from 'components/setting/plan-tab/PaymentSuccessful'
import Setting from 'components/setting'
import Words from 'components/words/Words'
import Editor from 'components/editor'
import NorthStar from 'components/northstar'
import CreatorPerformance from 'components/creator-performance'
import TranslationsDashboard from 'components/translations-dashboard'
import { hapticImpact } from './utils/haptics'
import DesktopAppPromo from './containers/app-promo/DesktopAppPromo'
import AndroidAppBanner from './containers/app-promo/AndroidAppBanner'

// Teachers only, so learners never download it: its own chunk, fetched when /dashboard opens.
const TeacherDesk = lazy(() => import('components/teacher-desk'))
const TeacherDeskRoute: React.FC = () => (
  <Suspense fallback={null}>
    <TeacherDesk />
  </Suspense>
)

const tagManagerArgs = {
  gtmId: REACT_APP_GTM_ID || '',
}

TagManager.initialize(tagManagerArgs)

function App() {
  const { credentials, membership } = AppUseSelector(({ user }) => user)
  const authToken = localStorage.getItem('authToken')

  window.dataLayer.push({
    event: 'pageview',
    page: {
      url: location,
      title: 'maayot • Your daily Chinese lesson',
    },
  })

  useEffect(() => {
    // Capture tracking parameters from URL on first visit
    const urlParams = new URLSearchParams(window.location.search)
    
    // Store GCLID (Google Click ID) - persists for 90 days.
    // Ads land on maayot.com and link here without a query string, so the URL
    // usually has no gclid. The Conversion Linker's `_gcl_aw` cookie is set on
    // .maayot.com and readable here, so fall back to it — see utils/attribution.
    const gclid = urlParams.get('gclid') || gclidFromCookie()
    if (gclid) {
      localStorage.setItem('gclid', gclid)
      localStorage.setItem('gclid_timestamp', Date.now().toString())
    }
    
    // Store FBCLID (Facebook Click ID), same fallback via the `_fbc` cookie.
    const fbclid = urlParams.get('fbclid') || fbclidFromCookie()
    if (fbclid) {
      localStorage.setItem('fbclid', fbclid)
      localStorage.setItem('fbclid_timestamp', Date.now().toString())
    }

    // Keep the raw `_fbc` (it carries the real click time) and `_fbp` so the
    // backend can send Meta a properly formed fbc on the Purchase event.
    // Never overwrite a stored fbc with a cookie for a different click.
    const fbc = fbcFromCookie()
    if (fbc && (!fbclid || fbc.endsWith('.' + fbclid))) localStorage.setItem('fbc', fbc)
    const fbp = fbpFromCookie()
    if (fbp) localStorage.setItem('fbp', fbp)
    
    // Store UTM params
    const utmParams = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']
    utmParams.forEach(param => {
      const value = urlParams.get(param)
      if (value) localStorage.setItem(param, value)
    })
  }, [])

  // App-wide haptic feedback: a subtle tap whenever the user presses a button,
  // link or other interactive control. Richer cues (success/error) are fired
  // explicitly at the moments that warrant them. No-ops on devices without the
  // Vibration API (e.g. iOS) — see utils/haptics. Disabled controls are skipped.
  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as HTMLElement | null
      const control = target?.closest?.('button, a, [role="button"], [role="tab"], input[type="submit"]')
      if (!control) return
      if (control.getAttribute('aria-disabled') === 'true' || (control as HTMLButtonElement).disabled) return
      hapticImpact('light')
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [])

  useEffect(() => {
    if (authToken && credentials?.memberId) {
      posthog.identify(`${credentials?.memberId}`, {
        memberId: credentials?.memberId,
        name: credentials?.metaData?.firstName || credentials?.customFields?.['first-name'],
        planName: getPlanInfo(membership?.name)?.title || 'Free',
        // Weekly launch-test segmentation (U9): every chart can be split by test group.
        weekly_test_group: (credentials?.customFields as any)?.weekly_test_group || 'none',
        is_weekly: membership?.name === membershipNameType.Weekly,
        // The learner's chosen language (null until chosen). The interface language is the
        // `ui_language` super property, see i18n/index.ts.
        native_language: credentials?.customFields?.native_language ?? null,
      })
    }
  }, [authToken, membership, credentials?.customFields?.native_language])

  return (
    <div className='App'>
      <AndroidAppBanner />
      <Routes>
        <>
          <Route path={LOGIN} element={<GuestRoute component={Login} />} />
          {/* Auto-login links from our emails. Deliberately neither GuestRoute (which would
              bounce an authed reader to /stories and lose the page the email pointed at) nor
              PrivateRoute (the reader is logged out - that is what this page fixes). */}
          <Route path={EMAIL_LINK_LOGIN} element={<EmailLinkLogin />} />
          {/* Legacy /learn-chinese/* onboarding URLs redirect straight to /signup.
              The explicit /new, /existing and /lead routes are required because the
              dynamic /:id/new (etc.) routes below outrank a bare /learn-chinese/*
              splat in React Router's route-ranking. The query string is preserved so
              attribution params (utm_*, gclid, fbclid) survive the redirect. */}
          <Route path='/learn-chinese' element={<Navigate to={{ pathname: SIGNUP, search: window.location.search }} replace />} />
          <Route path='/learn-chinese/*' element={<Navigate to={{ pathname: SIGNUP, search: window.location.search }} replace />} />
          <Route path='/learn-chinese/new' element={<Navigate to={{ pathname: SIGNUP, search: window.location.search }} replace />} />
          <Route path='/learn-chinese/existing' element={<Navigate to={{ pathname: SIGNUP, search: window.location.search }} replace />} />
          <Route path='/learn-chinese/lead' element={<Navigate to={{ pathname: SIGNUP, search: window.location.search }} replace />} />
          {/* /signup and every legacy onboarding URL render the single test-first flow.
              OnBoarding owns its own auth redirects (an authed user mid-flow must NOT
              be bounced to /stories the way GuestRoute would). */}
          {/* Public, and deliberately neither GuestRoute nor PrivateRoute: a logged-in
              learner following the same ad should see the offer, not be bounced away. */}
          <Route path={OFFER} element={<Offer />} />
          <Route path={REFERRAL_LANDING} element={<ReferralLanding />} />
          <Route path={SIGNUP} element={<OnBoarding />} />
          <Route path={CHOOSE_LANGUAGE} element={<ChooseLanguage />} />
          <Route path={NEW_USER_PATH} element={<OnBoarding />} />
          <Route path={OLD_USER_PATH} element={<OnBoarding />} />
          <Route path={LEAD_USER_PATH} element={<OnBoarding />} />
          <Route path={HOME} element={<Navigate to={STORIES} />} />
          <Route path={USER} element={<Navigate to={STORIES} />} />
          <Route path={STORIES} element={<PrivateRoute component={Stories} />} />
          <Route path={INTRODUCTION} element={<PrivateRoute component={Introduction} showNav />} />
          <Route path={`${STORY}/:id`} element={<PrivateRoute component={Story} showNav />} />
          <Route path={`${QUIZ}/:id`} element={<PrivateRoute component={Quiz} showNav />} />
          <Route path={`${QUESTION}/:id`} element={<PrivateRoute component={Question} showNav />} />
          <Route path={STORY_FINISHED} element={<PrivateRoute component={StoryFinished} showNav />} />
          {/* The featured-lessons archive is gone; old links land on the learner's history. */}
          <Route path='/past-stories' element={<Navigate to={HISTORY} replace />} />
          <Route path={HISTORY} element={<PrivateRoute component={History} />} />
          <Route path={WORDS} element={<PrivateRoute component={Words} />} />
          <Route path={CORRECTIONS} element={<PrivateRoute component={Corrections} />} />
          <Route path={`${DISCUSSION}/:storyId`} element={<PrivateRoute component={DiscussionPage} />} />
          <Route path={TASTER} element={<PrivateRoute component={Taster} />} />
          {/* Opened from the Scorecard email through the learner's login link; it draws its own header. */}
          <Route path={SCORECARD} element={<PrivateRoute component={Scorecard} showHeader={false} />} />
          {/* One-click claim links from the re-engagement emails: do their work on
              mount and redirect, so they render bare (no header) loading screens. */}
          <Route path={CLAIM_TRIAL} element={<PrivateRoute component={ClaimTrial} showHeader={false} />} />
          <Route path={CLAIM_OFFER} element={<PrivateRoute component={ClaimOffer} showHeader={false} />} />
          <Route path={CLAIM_TRIAL_OFFER} element={<PrivateRoute component={ClaimTrialOffer} showHeader={false} />} />
          <Route path={CLAIM_FREE_TRIAL} element={<PrivateRoute component={ClaimFreeTrial} />} />
          <Route path={SETTING} element={<PrivateRoute component={Setting} />} />
          <Route path={REFER} element={<PrivateRoute component={ReferPage} />} />
          <Route path={DASHBOARD} element={<PrivateRoute component={TeacherDeskRoute} showDashboard showHeader={false} />} />
          <Route path={DASHBOARD_CLASSIC} element={<PrivateRoute component={Dashboard} showDashboard showHeader={false} />} />
          <Route path={EDITOR} element={<PrivateRoute component={Editor} showDashboard showHeader={false} />} />
          <Route path={NORTHSTAR} element={<PrivateRoute component={NorthStar} showDashboard showHeader={false} />} />
          <Route path={CREATOR_PERFORMANCE} element={<PrivateRoute component={CreatorPerformance} showDashboard showHeader={false} />} />
          <Route
            path={TRANSLATIONS_REVIEW}
            element={<PrivateRoute component={TranslationsDashboard} showDashboard showHeader={false} />}
          />
          <Route path={CREATE_MEET} element={<PrivateRoute component={CreateMeet} isMeeting />} />
          <Route path={`${LIVE_MEET}/:id`} element={<PrivateRoute component={Meet} />} />
          <Route path={`${PRO_MEET}/:id`} element={<PrivateRoute component={Meet} />} />
          <Route
            path={PAYMENT_SUCCESSFUL}
            element={<PrivateRoute component={PaymentSuccessful} showHeader={false} />}
          />
          <Route path={PAYMENT_FAILED} element={<PrivateRoute component={PaymentFailed} showHeader={false} />} />
        </>

        <Route path={'/callback'} element={<SkritterCallback />} />
        <Route path={NOT_FOUND} element={<NotFound />} />
        <Route path='*' element={<Navigate to={NOT_FOUND} />} />
      </Routes>
      {/* {footerRender && <FooterOld />} */}
      <DesktopAppPromo />
    </div>
  )
}

export default App
